import { useMemo } from 'react';
import { useAuth } from './AuthProvider';

/**
 * User roles in the system:
 * - admin: Full access, can finalize reports (Amministratore/Ispettore)
 * - segreteria: Full access EXCEPT finalizing reports (Secretary)
 *
 * Role is stored in Supabase auth.users.raw_app_meta_data.role
 */
export type UserRole = 'admin' | 'segreteria';

interface UserRoleInfo {
  /** The user's role, defaults to 'admin' if not set */
  role: UserRole;
  /** Whether the user can finalize reports */
  canFinalizeReports: boolean;
  /** Whether the user has full access (admin role) */
  isAdmin: boolean;
  /** Whether the user is in segreteria role */
  isSegreteria: boolean;
}

/**
 * Hook to access the current user's role and permissions.
 *
 * Role is read from Supabase user metadata (app_metadata.role or user_metadata.role).
 * If no role is set, defaults to 'admin' for backwards compatibility.
 */
export function useUserRole(): UserRoleInfo {
  const { user } = useAuth();

  return useMemo(() => {
    // Check app_metadata first (set by admin), then user_metadata
    const rawRole =
      (user?.app_metadata?.role as string | undefined) ??
      (user?.user_metadata?.role as string | undefined);

    // Validate and default to admin
    const role: UserRole = rawRole === 'segreteria' ? 'segreteria' : 'admin';

    return {
      role,
      canFinalizeReports: role === 'admin',
      isAdmin: role === 'admin',
      isSegreteria: role === 'segreteria',
    };
  }, [user]);
}

/**
 * Get role display name in Italian
 */
export function getRoleDisplayName(role: UserRole): string {
  return role === 'admin' ? 'Amministratore' : 'Segreteria';
}
